import { create } from 'zustand';
import {
  GOAL_TO_TOOL_MAP,
  GOAL_LABELS,
  VULNERABILITY_RESPONSES,
  QUICK_TOUR_SECTIONS_WITH_TOOLS,
  QUICK_TOUR_SECTIONS_WITHOUT_TOOLS,
  EMPATHY_TOUR_STEPS,
} from '../config/onboarding-steps';
import {
  safeGetItem,
  safeSetItem,
  safeRemoveItem,
  safeGetJSON,
  safeSetJSON,
} from '../utils/storage';
import { useGiraffeTranslatorStore } from './giraffe-translator.store';
import { useNeedsGuesserStore } from './needs-guesser.store';
import { onSessionChange } from './session-read';
import type {
  OnboardingGoal,
  OnboardingTool,
  OnboardingStepId,
  QuickTourSection,
  QuickTourPhase,
  GiraffeHintStage,
  NeedsGuesserHintStage,
  OnboardingPersistedState,
  JourneyMemory,
} from '../types/onboarding';

// ============================================
// Constants
// ============================================

const STORAGE_KEY = 'onboarding_state';
const COMPLETED_KEY = 'onboarding_completed';
const SKIPPED_KEY = 'onboarding_skipped';
const JOURNEY_MEMORY_KEY = 'journey_memory';
const EMPATHY_TOUR_COMPLETE_KEY = 'empathy_tour_complete';

// ============================================
// Type Definitions
// ============================================

interface OnboardingState {
  isActive: boolean;
  currentStep: OnboardingStepId;
  selectedGoal: OnboardingGoal | null;
  mappedTool: OnboardingTool | null;
  vulnerabilityAccepted: boolean | null;
  quickTourIndex: number;
  quickTourPhase: QuickTourPhase;
  toolHintStage: GiraffeHintStage | NeedsGuesserHintStage | null;
  empathyTourActive: boolean;
  empathyTourIndex: number;
}

interface OnboardingActions {
  startOnboarding: () => void;
  skipOnboarding: () => void;
  completeOnboarding: () => void;
  resumeOnboarding: () => void;
  shouldShowOnboarding: (
    subscriptionPlatform: string | undefined,
    celebrationIsOpen: boolean,
    trialModalActive: boolean,
    verificationPending: boolean,
  ) => boolean;
  goToStep: (stepId: OnboardingStepId) => void;
  selectGoal: (goal: OnboardingGoal) => void;
  acceptVulnerability: () => void;
  declineVulnerability: () => void;
  completeGuidedTool: () => void;
  advanceQuickTour: () => void;
  handleTourNavClick: (section: QuickTourSection) => void;
  getCurrentQuickTourSection: () => QuickTourSection | null;
  getQuickTourSections: () => QuickTourSection[];
  getVulnerabilityResponse: () => string | null;
  setToolHintStage: (stage: GiraffeHintStage | NeedsGuesserHintStage) => void;
  startEmpathyTour: () => void;
  advanceEmpathyTour: () => void;
  skipEmpathyTour: () => void;
  persist: () => void;
}

type OnboardingStore = OnboardingState & OnboardingActions;

// ============================================
// Store Implementation
// ============================================

export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  isActive: false,
  currentStep: 'welcome',
  selectedGoal: null,
  mappedTool: null,
  vulnerabilityAccepted: null,
  quickTourIndex: 0,
  quickTourPhase: 'intro' as QuickTourPhase,
  toolHintStage: null,
  empathyTourActive: false,
  empathyTourIndex: 0,

  startOnboarding: () => {
    set({
      isActive: true,
      currentStep: 'welcome',
      selectedGoal: null,
      mappedTool: null,
      vulnerabilityAccepted: null,
      quickTourIndex: 0,
      quickTourPhase: 'intro' as QuickTourPhase,
      toolHintStage: null,
    });
    get().persist();
  },

  skipOnboarding: () => {
    // Capture journey memory before clearing state (goal only, no tool input)
    const { selectedGoal, mappedTool } = get();
    if (selectedGoal && !safeGetJSON(JOURNEY_MEMORY_KEY, null)) {
      safeSetJSON<JourneyMemory>(JOURNEY_MEMORY_KEY, {
        selectedGoal,
        goalLabel: GOAL_LABELS[selectedGoal],
        toolUsed: mappedTool || 'giraffe-translator',
        toolInputText: '',
        savedAt: new Date().toISOString(),
      });
    }

    set({
      isActive: false,
      currentStep: 'welcome',
      selectedGoal: null,
      mappedTool: null,
      vulnerabilityAccepted: null,
      quickTourIndex: 0,
      quickTourPhase: 'intro' as QuickTourPhase,
      toolHintStage: null,
    });
    safeSetItem(SKIPPED_KEY, 'true');
    safeRemoveItem(STORAGE_KEY);
  },

  completeOnboarding: () => {
    // Capture journey memory before clearing state (fallback if guided tool was skipped)
    const { selectedGoal, mappedTool } = get();
    if (selectedGoal && !safeGetJSON(JOURNEY_MEMORY_KEY, null)) {
      safeSetJSON<JourneyMemory>(JOURNEY_MEMORY_KEY, {
        selectedGoal,
        goalLabel: GOAL_LABELS[selectedGoal],
        toolUsed: mappedTool || 'giraffe-translator',
        toolInputText: '',
        savedAt: new Date().toISOString(),
      });
    }

    set({
      isActive: false,
      currentStep: 'complete',
      selectedGoal: null,
      mappedTool: null,
      vulnerabilityAccepted: null,
      quickTourIndex: 0,
      quickTourPhase: 'intro' as QuickTourPhase,
      toolHintStage: null,
    });
    safeSetItem(COMPLETED_KEY, 'true');
    safeRemoveItem(STORAGE_KEY);
  },

  resumeOnboarding: () => {
    const saved = safeGetJSON<OnboardingPersistedState | null>(STORAGE_KEY, null);
    if (!saved || !saved.isActive) return;

    set({
      isActive: true,
      currentStep: saved.currentStep,
      selectedGoal: saved.selectedGoal,
      mappedTool: saved.mappedTool,
      vulnerabilityAccepted: saved.vulnerabilityAccepted,
      quickTourIndex: saved.quickTourIndex,
      quickTourPhase: saved.quickTourPhase || 'intro',
      toolHintStage: saved.toolHintStage,
    });
  },

  shouldShowOnboarding: (
    subscriptionPlatform,
    celebrationIsOpen,
    trialModalActive,
    verificationPending,
  ) => {
    // A new account walled behind VerifyEmailGate has nothing to be toured
    // THROUGH — the gate replaces the whole app. The overlay is mounted at the
    // router root, outside the gate's early return, so the gate cannot suppress
    // it; the rule belongs here instead, where every future full-screen gate
    // gets it for free.
    if (verificationPending) return false;
    if (subscriptionPlatform !== 'trial') return false;
    if (safeGetItem(COMPLETED_KEY) === 'true') return false;
    if (safeGetItem(SKIPPED_KEY) === 'true') return false;
    if (celebrationIsOpen) return false;
    if (trialModalActive) return false;
    return true;
  },

  goToStep: (stepId: OnboardingStepId) => {
    set({ currentStep: stepId });
    get().persist();
  },

  selectGoal: (goal: OnboardingGoal) => {
    const tool = GOAL_TO_TOOL_MAP[goal];
    set({
      selectedGoal: goal,
      mappedTool: tool,
    });
    get().persist();
  },

  acceptVulnerability: () => {
    // Both tools start at 'page-load' — no branch needed
    const initialHintStage = 'page-load';
    set({
      vulnerabilityAccepted: true,
      currentStep: 'guided-tool',
      toolHintStage: initialHintStage as GiraffeHintStage | NeedsGuesserHintStage,
    });
    get().persist();
  },

  declineVulnerability: () => {
    set({
      vulnerabilityAccepted: false,
      currentStep: 'quick-tour',
      quickTourIndex: 0,
      quickTourPhase: 'intro' as QuickTourPhase,
      toolHintStage: null,
    });
    get().persist();
  },

  completeGuidedTool: () => {
    // Capture journey memory with tool input before transitioning
    const { selectedGoal, mappedTool } = get();
    if (selectedGoal && mappedTool) {
      let toolInputText = '';
      if (mappedTool === 'giraffe-translator') {
        toolInputText = useGiraffeTranslatorStore.getState().inputText || '';
      } else {
        toolInputText = useNeedsGuesserStore.getState().inputSentence || '';
      }
      safeSetJSON<JourneyMemory>(JOURNEY_MEMORY_KEY, {
        selectedGoal,
        goalLabel: GOAL_LABELS[selectedGoal],
        toolUsed: mappedTool,
        toolInputText,
        savedAt: new Date().toISOString(),
      });
    }

    set({
      currentStep: 'quick-tour',
      quickTourIndex: 0,
      quickTourPhase: 'intro' as QuickTourPhase,
      toolHintStage: null,
    });
    get().persist();
  },

  advanceQuickTour: () => {
    const { quickTourIndex, quickTourPhase } = get();
    const sections = get().getQuickTourSections();

    if (quickTourPhase === 'intro') {
      const currentSection = sections[quickTourIndex];
      // Empathy is locked — skip detail, go straight to next feature's intro
      if (currentSection === 'empathy') {
        const nextIndex = quickTourIndex + 1;
        if (nextIndex >= sections.length) {
          get().completeOnboarding();
          return;
        }
        set({ quickTourIndex: nextIndex, quickTourPhase: 'intro' });
        get().persist();
        return;
      }
      // Advance from intro to detail
      set({ quickTourPhase: 'detail' });
      get().persist();
      return;
    }

    // Phase is 'detail' — move to next feature's intro
    const nextIndex = quickTourIndex + 1;
    if (nextIndex >= sections.length) {
      get().completeOnboarding();
      return;
    }
    set({ quickTourIndex: nextIndex, quickTourPhase: 'intro' });
    get().persist();
  },

  handleTourNavClick: (section: QuickTourSection) => {
    const { currentStep, quickTourPhase } = get();
    if (currentStep !== 'quick-tour' || quickTourPhase !== 'intro') return;
    const currentSection = get().getCurrentQuickTourSection();
    if (section === currentSection) {
      set({ quickTourPhase: 'detail' });
      get().persist();
    }
  },

  getCurrentQuickTourSection: () => {
    const { currentStep, quickTourIndex } = get();
    if (currentStep !== 'quick-tour') return null;
    const sections = get().getQuickTourSections();
    return sections[quickTourIndex] || null;
  },

  getQuickTourSections: () => {
    const { vulnerabilityAccepted } = get();
    return vulnerabilityAccepted
      ? QUICK_TOUR_SECTIONS_WITH_TOOLS
      : QUICK_TOUR_SECTIONS_WITHOUT_TOOLS;
  },

  getVulnerabilityResponse: () => {
    const { selectedGoal } = get();
    if (!selectedGoal) return null;
    return VULNERABILITY_RESPONSES[selectedGoal];
  },

  setToolHintStage: (stage) => {
    set({ toolHintStage: stage });
    get().persist();
  },

  startEmpathyTour: () => {
    // Don't start if already completed
    if (safeGetItem(EMPATHY_TOUR_COMPLETE_KEY) === 'true') return;
    set({ empathyTourActive: true, empathyTourIndex: 0 });
  },

  advanceEmpathyTour: () => {
    const { empathyTourIndex } = get();
    const nextIndex = empathyTourIndex + 1;
    if (nextIndex >= EMPATHY_TOUR_STEPS.length) {
      // Tour complete
      set({ empathyTourActive: false, empathyTourIndex: 0 });
      safeSetItem(EMPATHY_TOUR_COMPLETE_KEY, 'true');
      return;
    }
    set({ empathyTourIndex: nextIndex });
  },

  skipEmpathyTour: () => {
    set({ empathyTourActive: false, empathyTourIndex: 0 });
    safeSetItem(EMPATHY_TOUR_COMPLETE_KEY, 'true');
  },

  persist: () => {
    const {
      currentStep,
      selectedGoal,
      mappedTool,
      vulnerabilityAccepted,
      quickTourIndex,
      quickTourPhase,
      toolHintStage,
      isActive,
    } = get();
    safeSetJSON<OnboardingPersistedState>(STORAGE_KEY, {
      currentStep,
      selectedGoal,
      mappedTool,
      vulnerabilityAccepted,
      quickTourIndex,
      quickTourPhase,
      toolHintStage,
      isActive,
    });
  },
}));

// The journey memory holds the text the account typed into its guided tool
// (`toolInputText`), kept in localStorage for later celebrations. It is that
// account's own words (David, 2026-10-04): when a signed-in session ends (a
// sign-out, or a switch to another account) it goes. Not on a sign-IN from no
// session: a guest converting to an account keeps the memory the tour made.
onSessionChange((_next, previous) => {
  if (previous) safeRemoveItem(JOURNEY_MEMORY_KEY);
});
